import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Phone, Globe, Clock, Mail, ArrowLeft, Award, BadgeCheck, Pencil, ShieldCheck, ClipboardCheck, Navigation, DoorOpen } from "lucide-react";
import { CATEGORIES } from "@/data/constants";
import CategoryIcon from "@/components/CategoryIcon";
import VenueMap from "@/components/VenueMap";
import SaveHeart from "@/components/SaveHeart";
import TierBadge from "@/components/TierBadge";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function VenueDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/venues/${id}`);
        setVenue(data);
      } catch (e) {
        setError("Venue not found");
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center text-gray-500">Loading…</div>;
  if (error || !venue) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <p className="text-lg text-gray-700">{error}</p>
      <Button asChild className="mt-4 rounded-full"><Link to="/search">Back to search</Link></Button>
    </div>
  );

  const cat = CATEGORIES.find(c => c.slug === venue.category);
  const hasPhotos = (venue.photo_paths || []).length > 0;
  const isOwner = user && venue.owner_id === user.id;
  const canClaim = user && !venue.owner_id && !isOwner;

  return (
    <article className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/search" className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-blue-700" data-testid="back-to-search">
        <ArrowLeft className="w-4 h-4"/> Back to results
      </Link>

      <header className="mt-6 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-50 text-blue-700 border border-blue-100 flex items-center justify-center">
              <CategoryIcon slug={venue.category} className="w-6 h-6"/>
            </div>
            <Badge className="rounded-full bg-gray-900 text-white">{cat?.short || venue.category}</Badge>
            <Badge variant="outline" className="rounded-full border-gray-300">{venue.area}</Badge>
            {venue.status === "pending" && <Badge variant="outline" className="rounded-full border-amber-300 bg-amber-50 text-amber-800">Pending review</Badge>}
          </div>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-gray-900">{venue.venue_name}</h1>
          {venue.nominated_by && venue.nominator_name && (
            <p className="mt-2 text-sm text-gray-600">
              Nominated by{" "}
              <Link to={`/nominator/${venue.nominated_by}`} className="text-blue-700 hover:underline font-medium" data-testid="nominator-link">
                {venue.nominator_name}
              </Link>
            </p>
          )}
          <div className="mt-4 flex items-center gap-4 flex-wrap">
            {(venue.rating_count || 0) > 0 || venue.tier ? (
              <TierBadge points={venue.rating} tier={venue.tier} size="lg" testId="venue-tier" />
            ) : (
              <span className="text-sm text-gray-500 font-medium uppercase tracking-widest">Awaiting inspection</span>
            )}
            <span className="text-sm text-gray-600">
              Based on <strong className="text-gray-900">{venue.rating_count || 0}</strong> inspection{(venue.rating_count || 0) === 1 ? "" : "s"}
            </span>
            {venue.owner_id && (
              <span className="inline-flex items-center gap-1 text-blue-800 bg-blue-50 border border-blue-100 rounded-full px-3 py-1 text-sm font-medium">
                <BadgeCheck className="w-4 h-4"/> Verified by venue
              </span>
            )}
            <SaveHeart venueId={venue.id} size="lg" />
          </div>

          {isOwner && (
            <div className="mt-6 border-2 border-blue-600 bg-blue-50 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-blue-900"><strong>You own this listing.</strong> Update opening hours, contact info and features.</div>
              <OwnerEditDialog venue={venue} onSaved={(v) => setVenue(v)} />
            </div>
          )}
          <p className="mt-6 text-lg text-gray-700 leading-relaxed">{venue.description}</p>

          <div className="mt-8">
            <TrustSignals venue={venue} user={user} />
          </div>

          <div className="mt-8">
            <h2 className="font-heading text-xl font-semibold">Accessible features</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {(venue.features || []).length === 0 ? <span className="text-gray-500">No features listed yet.</span> :
                venue.features.map(f => <Badge key={f} variant="outline" className="rounded-full border-2 border-gray-900 bg-white text-gray-900 px-3 py-1">{f}</Badge>)}
            </div>
          </div>

          {hasPhotos && (
            <div className="mt-8">
              <h2 className="font-heading text-xl font-semibold">Photos</h2>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                {venue.photo_paths.map(p => (
                  <img key={p} src={fileUrl(p)} alt="" className="w-full h-40 object-cover rounded-lg border border-gray-200"/>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="md:col-span-1">
          <Card className="border-2 border-gray-900 rounded-xl">
            <CardContent className="p-6 space-y-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Contact</div>
                <div className="mt-2 space-y-2 text-sm">
                  <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-gray-500"/><div>{venue.address}{venue.postcode ? `, ${venue.postcode}` : ""}</div></div>
                  {venue.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-500"/><a href={`tel:${venue.phone}`} className="text-blue-700 hover:underline">{venue.phone}</a></div>}
                  {venue.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-500"/><a href={`mailto:${venue.email}`} className="text-blue-700 hover:underline">{venue.email}</a></div>}
                  {venue.website && <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-gray-500"/><a href={venue.website} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline break-all">{venue.website}</a></div>}
                </div>
              </div>
              {venue.opening_hours && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Opening hours</div>
                  <div className="mt-2 text-sm flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 text-gray-500"/>{venue.opening_hours}</div>
                </div>
              )}
              {user && (user.role === "inspector" || user.role === "admin") && (
                <Button asChild className="w-full rounded-full bg-blue-600 hover:bg-blue-700" data-testid="start-inspection-btn">
                  <Link to={`/inspection/new?venue_id=${venue.id}`}>Start inspection</Link>
                </Button>
              )}
              {canClaim && <ClaimDialog venueId={venue.id} venueName={venue.venue_name} />}
              {!user && (
                <Button asChild variant="outline" className="w-full rounded-full border-2 border-gray-900">
                  <Link to="/login">Sign in to save or claim</Link>
                </Button>
              )}
            </CardContent>
          </Card>
        </aside>
      </header>

      {typeof venue.lat === "number" && typeof venue.lng === "number" && (
        <div className="mt-12 grid lg:grid-cols-[1fr_360px] gap-6">
          <div>
            <h2 className="font-heading text-2xl font-semibold mb-4">Location</h2>
            <VenueMap venues={[venue]} height={360} />
          </div>
          <RouteFromHere venue={venue} />
        </div>
      )}
    </article>
  );
}

function RouteFromHere({ venue }) {
  const isIOS = typeof navigator !== "undefined" && /iP(hone|ad|od)|Macintosh/.test(navigator.userAgent);
  const hasCoords = typeof venue.lat === "number" && typeof venue.lng === "number";
  const dest = hasCoords ? `${venue.lat},${venue.lng}` : "";
  const name = encodeURIComponent(venue.venue_name || "");
  const addressStr = [venue.address, venue.postcode].filter(Boolean).join(", ");
  const query = encodeURIComponent(addressStr || venue.venue_name || "");
  const googleUrl = hasCoords
    ? `https://www.google.com/maps/dir/?api=1&destination=${dest}&destination_place_id=&travelmode=driving`
    : `https://www.google.com/maps/dir/?api=1&destination=${query}`;
  const appleUrl = hasCoords
    ? `https://maps.apple.com/?daddr=${dest}&q=${name}`
    : `https://maps.apple.com/?daddr=${query}`;
  const buttons = [
    { key: "apple", label: "Open in Apple Maps", url: appleUrl },
    { key: "google", label: "Open in Google Maps", url: googleUrl },
  ];
  if (!isIOS) buttons.reverse();
  return (
    <Card className="border-2 border-gray-900 rounded-xl h-fit" data-testid="route-from-here">
      <CardContent className="p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center"><Navigation className="w-5 h-5"/></div>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-semibold">Getting there</h2>
            <p className="mt-1 text-sm text-gray-700">One tap opens turn-by-turn directions in your maps app.</p>
          </div>
        </div>
        {venue.accessible_entrance && (
          <div className="mt-4 rounded-lg border-2 border-blue-100 bg-blue-50 p-3 text-sm text-blue-900" data-testid="accessible-entrance-note">
            <div className="flex items-start gap-2">
              <DoorOpen className="w-4 h-4 mt-0.5 text-blue-700"/>
              <div><strong className="font-semibold">Accessible entrance:</strong> {venue.accessible_entrance}</div>
            </div>
          </div>
        )}
        <div className="mt-4 space-y-2">
          {buttons.map((b, i) => (
            <Button key={b.key} asChild
              variant={i === 0 ? "default" : "outline"}
              className={`w-full rounded-full ${i === 0 ? "bg-gray-900 hover:bg-black" : "border-2 border-gray-900"}`}
              data-testid={`route-${b.key}-btn`}>
              <a href={b.url} target="_blank" rel="noreferrer noopener">
                <Navigation className="w-4 h-4 mr-2"/>{b.label}
              </a>
            </Button>
          ))}
        </div>
        <p className="mt-3 text-xs text-gray-500">Destination: {addressStr || venue.venue_name}</p>
      </CardContent>
    </Card>
  );
}

function TrustSignals({ venue, user }) {
  const last = venue.last_inspected_at;
  const inspections = venue.rating_count || 0;
  let howLong = "";
  if (last) {
    const d = new Date(last);
    const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
    if (days === 0) howLong = "today";
    else if (days === 1) howLong = "yesterday";
    else if (days < 30) howLong = `${days} days ago`;
    else if (days < 365) howLong = `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? "" : "s"} ago`;
    else howLong = `${Math.floor(days / 365)} year${Math.floor(days / 365) === 1 ? "" : "s"} ago`;
  }
  return (
    <div className="rounded-xl border-2 border-gray-200 bg-gray-50 p-5" data-testid="trust-signals">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-gray-900 text-white flex items-center justify-center"><ShieldCheck className="w-5 h-5"/></div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-semibold">Inspection history</h2>
          {last ? (
            <p className="mt-1 text-sm text-gray-700">
              Last inspected <b data-testid="trust-last-date">{new Date(last).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</b>
              {" "}<span className="text-gray-500">({howLong})</span>. Based on {inspections} inspection{inspections === 1 ? "" : "s"}.
            </p>
          ) : (
            <p className="mt-1 text-sm text-gray-700">This venue is <b>awaiting its first inspection</b>. Our inspectors will grade it soon.</p>
          )}
          <div className="mt-4">
            {user
              ? <ReinspectDialog venueId={venue.id} venueName={venue.venue_name} />
              : <p className="text-xs text-gray-600 inline-flex items-center gap-1"><ClipboardCheck className="w-3.5 h-3.5"/><Link to="/login" className="text-blue-700 hover:underline font-medium">Sign in</Link>&nbsp;to request a re-inspection.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReinspectDialog({ venueId, venueName }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/venues/${venueId}/reinspect-request`, { reason });
      toast.success("Thanks — we'll notify our inspectors");
      setOpen(false);
      setReason("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send request");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full border-2 border-gray-900" data-testid="reinspect-open-btn">
          <ClipboardCheck className="w-4 h-4 mr-2"/>Request re-inspection
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request a re-inspection</DialogTitle>
          <DialogDescription>Noticed something has changed at {venueName}? Let us know so an inspector can revisit and update the grade.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="r-reason">What has changed? (optional)</Label>
            <Textarea id="r-reason" rows={4} value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. New accessible entrance opened, ramp removed, features improved…"
              className="mt-1 border-2 border-gray-300 focus:border-blue-600" data-testid="reinspect-reason"/>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="reinspect-submit">
              {busy ? "Sending…" : "Send request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ClaimDialog({ venueId, venueName }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/venues/${venueId}/claim`, {
        note, contact_name: contactName || null,
        contact_email: contactEmail || null,
        contact_phone: contactPhone || null,
      });
      toast.success("Claim submitted — we'll review it shortly");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit claim");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full rounded-full border-2 border-gray-900" data-testid="claim-venue-btn">
          <Award className="w-4 h-4 mr-2"/>Claim this venue
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Claim {venueName}</DialogTitle>
          <DialogDescription>
            Tell us who you are and how you're connected to this venue. An admin will review and approve.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="c-note">How are you connected?</Label>
            <Textarea id="c-note" rows={3} value={note} onChange={e => setNote(e.target.value)} required
              placeholder="e.g. Owner, general manager, accessibility lead…"
              className="mt-1 border-2 border-gray-300 focus:border-blue-600" data-testid="claim-note"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="c-name">Contact name</Label>
              <Input id="c-name" value={contactName} onChange={e => setContactName(e.target.value)} className="mt-1 border-2 border-gray-300 focus:border-blue-600"/>
            </div>
            <div>
              <Label htmlFor="c-phone">Contact phone</Label>
              <Input id="c-phone" value={contactPhone} onChange={e => setContactPhone(e.target.value)} className="mt-1 border-2 border-gray-300 focus:border-blue-600"/>
            </div>
          </div>
          <div>
            <Label htmlFor="c-email">Contact email</Label>
            <Input id="c-email" type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} className="mt-1 border-2 border-gray-300 focus:border-blue-600"/>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="claim-submit">
              {busy ? "Submitting…" : "Submit claim"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OwnerEditDialog({ venue, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    phone: venue.phone || "",
    email: venue.email || "",
    website: venue.website || "",
    opening_hours: venue.opening_hours || "",
    description: venue.description || "",
    accessible_entrance: venue.accessible_entrance || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch(`/venues/${venue.id}/owner`, form);
      toast.success("Listing updated");
      onSaved && onSaved(data);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="owner-edit-btn"><Pencil className="w-4 h-4 mr-2"/>Edit listing</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Update your listing</DialogTitle>
          <DialogDescription>You can edit contact details, opening hours and description. Photos, category and features are managed by our team to keep listings independent.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="o-phone">Phone</Label>
            <Input id="o-phone" value={form.phone} onChange={e => set("phone", e.target.value)} className="mt-1 border-2 border-gray-300"/>
          </div>
          <div>
            <Label htmlFor="o-email">Email</Label>
            <Input id="o-email" type="email" value={form.email} onChange={e => set("email", e.target.value)} className="mt-1 border-2 border-gray-300"/>
          </div>
          <div>
            <Label htmlFor="o-web">Website</Label>
            <Input id="o-web" value={form.website} onChange={e => set("website", e.target.value)} className="mt-1 border-2 border-gray-300"/>
          </div>
          <div>
            <Label htmlFor="o-hrs">Opening hours</Label>
            <Input id="o-hrs" value={form.opening_hours} onChange={e => set("opening_hours", e.target.value)} className="mt-1 border-2 border-gray-300"/>
          </div>
          <div>
            <Label htmlFor="o-desc">Description</Label>
            <Textarea id="o-desc" rows={4} value={form.description} onChange={e => set("description", e.target.value)} className="mt-1 border-2 border-gray-300"/>
          </div>
          <div>
            <Label htmlFor="o-entrance">Accessible entrance note</Label>
            <Textarea id="o-entrance" rows={2} value={form.accessible_entrance} onChange={e => set("accessible_entrance", e.target.value)}
              placeholder="e.g. Step-free entrance on Foo Street, buzzer for assistance"
              className="mt-1 border-2 border-gray-300" data-testid="owner-entrance-input"/>
            <p className="mt-1 text-xs text-gray-500">Helps visitors head straight to the right door.</p>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full bg-blue-600 hover:bg-blue-700">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
